/**
 * Phase 18d (v2) — Strategy Memory hover badge using shadcn HoverCard.
 *
 * The v1 implementation used plain `onMouseEnter`/`onMouseLeave` + a
 * conditionally-rendered absolutely-positioned span. That worked in the
 * dev preview but the e1_tester environment couldn't observe the popover
 * — no Radix portal, no `role="tooltip"`, and synthetic mouse events
 * from automation sometimes missed the raw handlers.
 *
 * v2 switches to shadcn's HoverCard (Radix under the hood) with
 * `openDelay=200` (matches the debounce spec) and `closeDelay=100`. The
 * fetch is triggered on `onOpenChange(true)` — no manual timer needed —
 * with the 200ms delay already handled by Radix.
 *
 * Data-testid contract (unchanged from v1 so existing tests continue):
 *   sm-badge-<id>            — trigger wrapper (hoverable)
 *   sm-badge-popover-<id>    — content root
 *   sm-badge-title-<id>      — title line
 *   sm-badge-evidence-<id>   — evidence line
 *   sm-badge-created-<id>    — relative timestamp
 *   sm-badge-agent-<id>      — agent_source line (when present)
 *   sm-badge-superseded-<id> — supersede pill (when present)
 *   sm-badge-skeleton-<id>   — loading skeleton
 */
import React, { useRef, useState } from "react";
import {
  HoverCard, HoverCardContent, HoverCardTrigger,
} from "../ui/hover-card";
import { api } from "../../lib/api";

// Module-scoped LRU cache. Last-20 entries by id, MRU semantics via
// delete-then-set on Map insertion order.
const LRU_MAX = 20;
const lru = new Map();
function lruGet(id) {
  if (!lru.has(id)) return undefined;
  const v = lru.get(id); lru.delete(id); lru.set(id, v);
  return v;
}
function lruSet(id, v) {
  if (lru.has(id)) lru.delete(id);
  lru.set(id, v);
  if (lru.size > LRU_MAX) lru.delete(lru.keys().next().value);
}
export function __clearSMLRU() { lru.clear(); }              // test-only

function truncate(s, n = 280) {
  if (!s) return "";
  return s.length <= n ? s : `${s.slice(0, n).trimEnd()}…`;
}
function relativeAgo(iso) {
  if (!iso) return "";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${Math.floor(s)}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export function StrategyMemoryBadge({ entryId, label, className }) {
  const [entry, setEntry] = useState(() => lruGet(entryId));
  const [loading, setLoading] = useState(false);
  const abortRef = useRef(null);

  const handleOpenChange = (open) => {
    if (!open) {
      if (abortRef.current) { abortRef.current.abort(); abortRef.current = null; }
      return;
    }
    if (entry) return;                                        // cache hit
    const cached = lruGet(entryId);
    if (cached) { setEntry(cached); return; }
    setLoading(true);
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    api.get(`/strategy-memory/entries/${entryId}`, { signal: ctrl.signal })
      .then((r) => { lruSet(entryId, r.data); setEntry(r.data); })
      .catch(() => { /* honest: silent — badge still visible */ })
      .finally(() => setLoading(false));
  };

  return (
    <HoverCard openDelay={200} closeDelay={100}
                  onOpenChange={handleOpenChange}>
      <HoverCardTrigger asChild>
        <span
          data-testid={`sm-badge-${entryId}`}
          className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs bg-accent/40 hover:bg-accent cursor-help font-mono ${className || ""}`}
        >
          SM · {label || entryId.slice(0, 10)}
        </span>
      </HoverCardTrigger>
      <HoverCardContent
        data-testid={`sm-badge-popover-${entryId}`}
        role="tooltip"
        side="bottom" align="start"
        className="w-80 text-xs space-y-1.5"
      >
        {loading && !entry && (
          <div data-testid={`sm-badge-skeleton-${entryId}`}
                className="space-y-1.5">
            <div className="h-3 w-3/4 rounded bg-muted animate-pulse" />
            <div className="h-3 w-full rounded bg-muted animate-pulse" />
            <div className="h-3 w-1/2 rounded bg-muted animate-pulse" />
          </div>
        )}
        {entry && (
          <>
            <div data-testid={`sm-badge-title-${entryId}`}
                  className="font-semibold text-sm text-foreground">
              {entry.title || entry.claim || entryId}
            </div>
            <div data-testid={`sm-badge-evidence-${entryId}`}
                  className="text-muted-foreground">
              {truncate(entry.evidence
                          || entry.reasoning
                          || entry.summary
                          || "—", 280)}
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pt-1">
              <span data-testid={`sm-badge-created-${entryId}`}
                      className="text-muted-foreground">
                {relativeAgo(entry.created_at)}
              </span>
              {entry.agent_source && (
                <span data-testid={`sm-badge-agent-${entryId}`}
                        className="text-muted-foreground">
                  agent:{" "}
                  <span className="font-mono">{entry.agent_source}</span>
                </span>
              )}
              {entry.superseded_by && (
                <span data-testid={`sm-badge-superseded-${entryId}`}
                        className="inline-flex items-center rounded-full border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 dark:text-amber-200">
                  superseded → {String(entry.superseded_by).slice(0, 10)}
                </span>
              )}
            </div>
          </>
        )}
      </HoverCardContent>
    </HoverCard>
  );
}

export default StrategyMemoryBadge;
